import { useRef, useState, useEffect } from 'react';
import { FlatList } from 'react-native';

import { scale } from '@components/frame';
import { isWeb } from '@helpers';
import { CHART_TYPE } from '@v2/pages/common/constants/base.constants';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { useAnimatedChart } from './useAnimatedChart';
import { UseManageChartParams, UseManageChartReturn } from '@components/machines/components/eventsChart/types';

export const useManageChart = ({
  fullWidth,
  setChartType,
  showLine,
  selectedChartDate,
  setSelectedChartDate,
  resetMobileChartCount,
  id,
  isActive,
}: UseManageChartParams): UseManageChartReturn => {
  const { IS_NARROW_WEB } = useDeviceSize();
  const { timePeriod: selectedTime } = useHandleParams();
  const flatListRef = useRef<FlatList>(null);
  const padding = scale(15);
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldDisplayLegends = !isWeb && !showLine;

  const chartHorizontalOffset = shouldDisplayLegends ? 15 : isWeb ? 60 : 30;
  const flatListItemWidth = fullWidth - scale(chartHorizontalOffset, false);
  const legendsWidth = shouldDisplayLegends ? scale(40) : 0;
  const chartHeight = scale(119, false);
  const rightDashedLineOffsetForVisibility = scale(shouldDisplayLegends ? 2 : 0);
  const chartWidth = fullWidth - legendsWidth - chartHorizontalOffset - rightDashedLineOffsetForVisibility;

  const triggerLine = () => {
    setChartType(showLine ? CHART_TYPE.BAR : CHART_TYPE.LINE);
    setShowInitialLoader(showLine);
  };

  const { config, renderChart, renderChartPrev, saveChartData, saveChartDataPrev, selectedFlatListIndex, userStopScroll } = useAnimatedChart({
    flatListItemWidth,
    flatListRef,
    id,
    selectedChartDate,
    selectedTime,
    setSelectedChartDate,
    showLine,
    resetMobileChartCount,
  });

  const dataBar = renderChart?.[selectedFlatListIndex]?.data?.dataBar;

  const heightValues = isActive ? ['active'] : ['setup', 'change', 'active'];

  const readyToDisplayLegends = shouldDisplayLegends && !!dataBar?.length && !showInitialLoader;

  const loaderContainerStyles = {
    marginRight: !readyToDisplayLegends ? padding * 3.5 : 0,
  };

  // Handle initial loader for mobile bar chart (Bar Chart has flickering on initial render)
  useEffect(() => {
    if (!isWeb && !showLine && dataBar) {
      // Clear any existing timeout
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }

      // Set timeout to hide loader after 300ms
      loaderTimeoutRef.current = setTimeout(() => {
        setShowInitialLoader(false);
      }, 100);
    }

    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, [isWeb, showLine, !!dataBar]);

  return {
    flatListRef,
    padding,
    showInitialLoader,
    shouldDisplayLegends,
    IS_NARROW_WEB,
    selectedTime,
    chartHorizontalOffset,
    flatListItemWidth,
    legendsWidth,
    chartHeight,
    rightDashedLineOffsetForVisibility,
    chartWidth,
    triggerLine,
    config,
    renderChart,
    renderChartPrev,
    saveChartData,
    saveChartDataPrev,
    selectedFlatListIndex,
    userStopScroll,
    dataBar,
    heightValues,
    readyToDisplayLegends,
    loaderContainerStyles,
  };
};
